import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { formatDuration, mediaUrl } from "@/lib/format";
import { CategoryThumb } from "@/components/CategoryThumb";
import { VerifiedShield } from "@/components/VerifiedShield";
import { PaymentBadges } from "@/components/PaymentBadges";
import { useCategoryMeta, categoryIconName } from "@/lib/categoryMeta";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Clock, Search, Tag, Boxes, Sparkles, MapPin, Repeat, LocateFixed, Home, Briefcase, Navigation, Heart } from "lucide-react";
import * as Lucide from "lucide-react";
import { toast } from "sonner";

export default function Browse() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [cat, setCatState] = useState(() => searchParams.get("category") || "All");
  const setCat = (c) => {
    setCatState(c);
    const next = new URLSearchParams(searchParams);
    if (c === "All") next.delete("category"); else next.set("category", c);
    setSearchParams(next, { replace: true });
  };
  const [area, setArea] = useState(() => localStorage.getItem("bookthub_area") || "All areas");
  const [loading, setLoading] = useState(true);
  const [coords, setCoords] = useState(null);      // {lat,lng}
  const [locLabel, setLocLabel] = useState("");     // "GPS" | "Home" | "Work"
  const [radius, setRadius] = useState(0);          // miles, 0 = any distance
  const [locating, setLocating] = useState(false);
  const [addrOpen, setAddrOpen] = useState(false);
  const [home, setHome] = useState({ line1: "", city: "", state: "", zip: "" });
  const [work, setWork] = useState({ line1: "", city: "", state: "", zip: "" });
  const [savingAddr, setSavingAddr] = useState(false);
  const [likes, setLikes] = useState(new Set());
  const meta = useCategoryMeta();

  const load = () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (cat !== "All") params.category = cat;
    if (coords) { params.lat = coords.lat; params.lng = coords.lng; if (radius) params.radius = radius; }
    api.get("/services", { params }).then((r) => setServices(r.data)).finally(() => setLoading(false));
  };

  const geolocate = () => {
    if (!navigator.geolocation) { toast.error("Location isn't available in this browser."); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setLocLabel("GPS"); setLocating(false); toast.success("Using your current location."); },
      () => { setLocating(false); toast.error("Couldn't get your location. Allow location access or use a saved address."); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  useEffect(() => { if (user?.role === "customer") api.get("/me/likes").then((r) => setLikes(new Set(r.data))).catch(() => {}); }, [user]);

  const toggleLike = async (e, sid) => {
    e.preventDefault(); e.stopPropagation();
    try {
      const { data } = await api.post(`/services/${sid}/like`);
      setLikes((prev) => { const n = new Set(prev); if (data.liked) { n.add(sid); toast.success("Saved to your likes."); } else n.delete(sid); return n; });
    } catch (err) { toast.error("Please log in to save services."); }
  };

  const applySaved = (geo, label) => {
    if (!geo || !geo.coordinates) { toast.error(`No ${label.toLowerCase()} address saved yet. Add one first.`); return; }
    setCoords({ lat: geo.coordinates[1], lng: geo.coordinates[0] }); setLocLabel(label);
  };

  const clearLoc = () => { setCoords(null); setLocLabel(""); };

  const saveAddresses = async () => {
    setSavingAddr(true);
    try {
      const { data } = await api.put("/me/addresses", { address: home, address2: work });
      refreshUser(data.user);
      toast.success("Addresses saved.");
      setAddrOpen(false);
    } catch (e) { toast.error("Couldn't save addresses."); }
    finally { setSavingAddr(false); }
  };

  useEffect(() => { api.get("/services/categories").then((r) => setCats(r.data)); }, []);
  useEffect(() => { api.get("/packages").then((r) => setPackages(r.data)).catch(() => {}); }, []);
  useEffect(() => { const c = searchParams.get("category") || "All"; if (c !== cat) setCatState(c); /* eslint-disable-next-line */ }, [searchParams]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cat, coords, radius]);
  // Logged-in customers: their saved city personalizes the area (profile wins over localStorage)
  useEffect(() => { if (user && user.city) setArea(user.city); }, [user]);
  // Prefill saved addresses into the dialog
  useEffect(() => {
    if (user?.address) setHome({ line1: user.address.line1 || "", city: user.address.city || "", state: user.address.state || "", zip: user.address.zip || "" });
    if (user?.address2) setWork({ line1: user.address2.line1 || "", city: user.address2.city || "", state: user.address2.state || "", zip: user.address2.zip || "" });
  }, [user]);

  const isCustomer = user && user.role === "customer";

  const areas = useMemo(() => {
    const set = new Set(packages.map((p) => (p.city || "").trim()).filter(Boolean));
    if (user && user.city) set.add(user.city.trim());
    return ["All areas", ...Array.from(set).sort()];
  }, [packages, user]);

  const bundledServiceIds = useMemo(() => {
    const s = new Set();
    packages.forEach((p) => p.items.forEach((i) => s.add(i.service_id)));
    return s;
  }, [packages]);

  const shownPackages = useMemo(
    () => (area === "All areas" ? packages : packages.filter((p) => (p.city || "").trim().toLowerCase() === area.toLowerCase())),
    [packages, area]
  );

  const pickArea = (v) => {
    setArea(v);
    localStorage.setItem("bookthub_area", v);
    if (isCustomer) {
      api.put("/me/city", { city: v === "All areas" ? null : v })
        .then((r) => refreshUser(r.data.user))
        .catch(() => {});
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tighter">Find a service</h1>
      <p className="text-muted-foreground mt-2">Browse services from independent providers and book a slot.</p>

      {/* Location / radius search bar */}
      <div className="mt-6 rounded-sm border border-border bg-white p-4" data-testid="location-bar">
        <div className="flex flex-wrap items-center gap-2">
          <span className="flex items-center gap-1.5 text-sm font-medium text-foreground"><Navigation className="w-4 h-4 text-primary" />Search near</span>
          <Button type="button" variant={locLabel === "GPS" ? "default" : "outline"} size="sm" className="rounded-sm" onClick={geolocate} disabled={locating} data-testid="use-gps-btn">
            <LocateFixed className="w-4 h-4 mr-1.5" />{locating ? "Locating…" : "My location"}
          </Button>
          {user?.geo && (
            <Button type="button" variant={locLabel === "Home" ? "default" : "outline"} size="sm" className="rounded-sm" onClick={() => applySaved(user.geo, "Home")} data-testid="use-home-btn">
              <Home className="w-4 h-4 mr-1.5" />Home
            </Button>
          )}
          {user?.geo2 && (
            <Button type="button" variant={locLabel === "Work" ? "default" : "outline"} size="sm" className="rounded-sm" onClick={() => applySaved(user.geo2, "Work")} data-testid="use-work-btn">
              <Briefcase className="w-4 h-4 mr-1.5" />Work
            </Button>
          )}
          {coords && (
            <>
              <select value={radius} onChange={(e) => setRadius(Number(e.target.value))} data-testid="radius-select"
                className="text-sm border border-border rounded-sm bg-white px-2 py-1.5 outline-none focus:border-secondary">
                <option value={0}>Any distance</option>
                <option value={5}>Within 5 mi</option>
                <option value={10}>Within 10 mi</option>
                <option value={25}>Within 25 mi</option>
                <option value={50}>Within 50 mi</option>
              </select>
              <span className="text-xs text-muted-foreground" data-testid="active-loc-label">Using: {locLabel}</span>
              <button onClick={clearLoc} className="text-xs text-primary underline" data-testid="clear-loc-btn">clear</button>
            </>
          )}
          {isCustomer && (
            <Dialog open={addrOpen} onOpenChange={setAddrOpen}>
              <DialogTrigger asChild>
                <Button type="button" variant="ghost" size="sm" className="rounded-sm ml-auto" data-testid="manage-addresses-btn"><MapPin className="w-4 h-4 mr-1.5" />My addresses</Button>
              </DialogTrigger>
              <DialogContent className="max-w-md rounded-sm" data-testid="addresses-dialog">
                <DialogHeader><DialogTitle className="font-display text-2xl">Your addresses</DialogTitle></DialogHeader>
                <p className="text-sm text-muted-foreground -mt-1">Save a home and an optional work address to quickly search near either.</p>
                {[["Home", home, setHome, "home"], ["Work (optional)", work, setWork, "work"]].map(([label, val, setter, tid]) => (
                  <div key={tid} className="space-y-2 border-t border-border pt-3">
                    <Label className="text-sm font-semibold">{label}</Label>
                    <Input value={val.line1} onChange={(e) => setter((s) => ({ ...s, line1: e.target.value }))} placeholder="Street address" className="rounded-sm" data-testid={`addr-${tid}-line1`} />
                    <div className="grid grid-cols-3 gap-2">
                      <Input value={val.city} onChange={(e) => setter((s) => ({ ...s, city: e.target.value }))} placeholder="City" className="rounded-sm col-span-1" data-testid={`addr-${tid}-city`} />
                      <Input value={val.state} onChange={(e) => setter((s) => ({ ...s, state: e.target.value }))} placeholder="State" className="rounded-sm col-span-1" data-testid={`addr-${tid}-state`} />
                      <Input value={val.zip} onChange={(e) => setter((s) => ({ ...s, zip: e.target.value }))} placeholder="ZIP" className="rounded-sm col-span-1" data-testid={`addr-${tid}-zip`} />
                    </div>
                  </div>
                ))}
                <DialogFooter><Button onClick={saveAddresses} disabled={savingAddr} className="rounded-sm w-full" data-testid="save-addresses-btn">{savingAddr ? "Saving…" : "Save addresses"}</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
        {!coords && <p className="text-xs text-muted-foreground mt-2">Tip: tap "My location" to find services closest to you.</p>}
      </div>

      {/* Packages in your area highlight */}
      {packages.length > 0 && (
        <section className="mt-10 rounded-sm border border-primary/30 bg-primary/[0.04] p-6" data-testid="packages-highlight">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-primary">
              <Boxes className="w-5 h-5" />
              <h2 className="font-display text-xl font-semibold tracking-tight text-foreground" data-testid="packages-highlight-title">
                {isCustomer && user.city ? `Packages near you in ${user.city}` : "Packages in your area"}
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-muted-foreground" />
              <select value={area} onChange={(e) => pickArea(e.target.value)} data-testid="area-select"
                className="text-sm border border-border rounded-sm bg-white px-2 py-1.5 outline-none focus:border-secondary">
                {areas.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              <Link to="/packages"><Button variant="ghost" size="sm" data-testid="see-all-packages">See all</Button></Link>
            </div>
          </div>
          {isCustomer && !user.city && (
            <p className="text-xs text-muted-foreground mt-2" data-testid="set-city-hint">
              Pick your area above — we'll remember it and personalize local packages for you next time.
            </p>
          )}

          {shownPackages.length === 0 ? (
            <p className="text-sm text-muted-foreground mt-4" data-testid="no-area-packages">No packages in {area} yet — try “All areas”.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-5">
              {shownPackages.slice(0, 6).map((p) => (
                <Link key={p.id} to={`/packages/${p.id}`} data-testid={`highlight-package-${p.id}`}
                  className="group bg-white border border-border rounded-sm p-5 hard-shadow-hover transition-all hover:-translate-y-[2px]">
                  {p.cover_url && <img src={mediaUrl(p.cover_url)} alt={p.name} className="w-full h-28 object-cover rounded-sm mb-3" />}
                  <div className="flex items-center justify-between">
                    <h3 className="font-display text-lg font-medium">{p.name}</h3>
                    {p.total_savings > 0 && <span className="text-xs font-bold text-primary bg-primary/10 rounded-sm px-2 py-0.5 flex items-center gap-1"><Sparkles className="w-3 h-3" />Save ${p.total_savings}</span>}
                  </div>
                  {p.city && <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" />{p.city}</p>}
                  <p className="text-sm text-muted-foreground mt-2 line-clamp-1">{p.items.map((i) => i.service_title).join(" + ")}</p>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                    <span className="text-xs text-muted-foreground">{p.items.length} services</span>
                    <span className="font-display text-base font-semibold text-primary">${p.total_price} <span className="text-xs text-muted-foreground line-through">${p.total_base}</span></span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      )}

      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="mt-10 flex gap-2 max-w-xl">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search services or providers…"
            className="pl-9 rounded-sm" data-testid="browse-search-input" />
        </div>
        <Button type="submit" className="rounded-sm" data-testid="browse-search-btn">Search</Button>
      </form>

      <div className="flex flex-wrap gap-2 mt-5">
        {["All", ...cats].map((c) => {
          const iconName = c === "All" ? "LayoutGrid" : categoryIconName(meta, c);
          const Ico = Lucide[iconName] || Lucide.Tag;
          return (
            <button key={c} onClick={() => setCat(c)} data-testid={`browse-cat-${c}`}
              className={`text-sm px-3 py-1.5 border rounded-sm transition-all inline-flex items-center gap-1.5 ${cat === c ? "bg-secondary text-white border-secondary" : "border-border hover:border-secondary"}`}>
              <Ico className="w-3.5 h-3.5" />{c}
            </button>
          );
        })}
      </div>

      {loading ? (
        <p className="text-muted-foreground mt-12">Loading…</p>
      ) : services.length === 0 ? (
        <p className="text-muted-foreground mt-12" data-testid="browse-empty">No services found.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
          {services.map((s) => (
            <Link key={s.id} to={`/service/${s.id}`} data-testid={`service-card-${s.id}`}
              className="group bg-white border border-border rounded-sm overflow-hidden hard-shadow-hover transition-all hover:-translate-y-[2px]">
              <div className="h-44 overflow-hidden bg-muted relative">
                <CategoryThumb imageUrl={s.image_url} category={s.category} testid={`service-image-${s.id}`}
                  imgClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                {bundledServiceIds.has(s.id) && (
                  <span className="absolute top-3 left-3 text-xs font-bold text-white bg-primary rounded-sm px-2 py-1 flex items-center gap-1" data-testid={`bundle-badge-${s.id}`}>
                    <Boxes className="w-3 h-3" />In a package
                  </span>
                )}
                {s.is_recurring && (
                  <span className="absolute top-3 right-3 text-xs font-bold text-white bg-secondary rounded-sm px-2 py-1 flex items-center gap-1 capitalize" data-testid={`recurring-badge-${s.id}`}>
                    <Repeat className="w-3 h-3" />{s.recurrence}
                  </span>
                )}
                {isCustomer && (
                  <button onClick={(e) => toggleLike(e, s.id)} data-testid={`like-btn-${s.id}`}
                    className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center hover:bg-white transition-colors shadow-sm">
                    <Heart className={`w-4 h-4 transition-colors ${likes.has(s.id) ? "fill-primary text-primary" : "text-foreground"}`} />
                  </button>
                )}
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Tag className="w-3 h-3" /> {s.category}
                </div>
                <h3 className="font-display text-xl font-medium mt-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{s.description || "—"}</p>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <span className="text-xs px-2 py-0.5 rounded-sm bg-muted text-muted-foreground flex items-center gap-1" data-testid={`loc-type-${s.id}`}>
                    <MapPin className="w-3 h-3" />{s.location_type === "customer" ? "At your location" : "At provider"}
                  </span>
                  {typeof s.distance_miles === "number" && (
                    <span className="text-xs px-2 py-0.5 rounded-sm bg-secondary/10 text-secondary font-medium flex items-center gap-1" data-testid={`distance-${s.id}`}>
                      <Navigation className="w-3 h-3" />{s.distance_miles} mi
                    </span>
                  )}
                  {s.service_type === "service_call" && (
                    <span className="text-xs px-2 py-0.5 rounded-sm bg-primary/10 text-primary font-medium flex items-center gap-1" data-testid={`service-call-${s.id}`}>
                      Service call
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                  <span className="flex items-center gap-1 text-sm text-muted-foreground"><Clock className="w-4 h-4" />{formatDuration(s.duration_minutes)}</span>
                  <span className="font-display text-lg font-semibold text-primary">{s.service_type === "service_call" ? <>from ${s.price}</> : <>${s.price}</>}</span>
                </div>
                {s.service_type === "service_call" && (
                  <p className="text-xs text-muted-foreground mt-1.5" data-testid={`service-call-note-${s.id}`}>${s.price} base fee (minimum) charged at booking · parts &amp; labor assessed on-site, credited toward your total.</p>
                )}
                <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5">
                  {s.subscriber_logo && <img src={mediaUrl(s.subscriber_logo)} alt="" className="w-4 h-4 rounded-full object-cover" />}
                  by{" "}
                  <span
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/p/${s.subscriber_id}`); }}
                    className="hover:text-primary hover:underline cursor-pointer"
                    data-testid={`provider-link-${s.subscriber_id}`}>
                    {s.subscriber_name}
                  </span>
                  {s.subscriber_verified && <VerifiedShield testid={`service-verified-${s.id}`} />}
                </p>
                <PaymentBadges methods={s.payment_methods} className="mt-2" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
